import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import SelectedElementsModal from 'components/modal/SelectedElementsModal'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from '../../../../components/card/ProgressCard'
import Input from '../../../../components/forms/Input'
import RadioButtons from '../../../../components/forms/RadioButtons'
import Select from '../../../../components/forms/Select'
import PiezometryAction from '../../../../piezometry/actions/PiezometryAction'
import CityAction from '../../../../referencial/components/city/actions/CityAction'
import ContributorAction from '../../../../referencial/components/contributor/actions/ContributorAction'
import NetworkAction from '../../../../referencial/components/network/actions/NetworkAction'
import Job from '../../../dto/DtoJob'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import { groupBy, mapValues, uniq } from 'lodash'
import { FILTERS, getStationModes } from 'utils/JobUtils'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import StationsPointPrelTable from '../../../../station/components/StationsPointPrelTable'
import StationAction from '../../../../station/actions/StationAction'
import { DefaultDialogTitle } from 'components/styled/Dialog'

const ALL = 'ALL'
const CUSTOM = 'CUSTOM'
const DIFF = 'DIFF'
const UPDATE_DATE = 'UPDATE_DATE'

const BRUTE = 'BRUTE'
const MIN = 'MIN'
const MAX = 'MAX'
const AVERAGE = 'AVERAGE'

const ExportQesoutPanel = ({
    job = {},
    onChangeJob = () => { },
    isEditMode = false,
}) => {
    const {
        piezometers,
        contributors,
        networks,
        cities,
        pointsPrelList,
        piezometersContributors,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometersLight,
        contributors: store.ContributorReducer.contributors,
        networks: store.NetworkReducer.networks,
        cities: store.CityReducer.cities,
        pointsPrelList: store.StationReducer.pointsPrelByType.piezometry || [],
        piezometersContributors: store.PiezometryReducer.piezometersContributors,
    }), shallowEqual)

    const [isOpen, setIsOpen] = useState(false)


    const exportModes = [
        { code: ALL, name: i18n.exportHistory },
        { code: CUSTOM, name: i18n.choiseDate },
        { code: DIFF, name: i18n.exportAccordingLastAutoExport },
        { code: UPDATE_DATE, name: i18n.exportWithUpdateDate },
    ]

    const groupModes = [
        { code: BRUTE, name: i18n.brute },
        { code: MIN, name: i18n.min },
        { code: MAX, name: i18n.max },
        { code: AVERAGE, name: i18n.average },
    ]

    const dispatch = useDispatch()

    const { progress, isLoaded } = useProgressDispatch(() => [
        dispatch(PiezometryAction.fetchPiezometersLight()),
        dispatch(ContributorAction.fetchContributors()),
        dispatch(NetworkAction.fetchNetworks()),
        dispatch(CityAction.fetchCities()),
        dispatch(StationAction.fetchStationPointsPrel(STATION_TYPE_NAME.piezometry)),
        dispatch(PiezometryAction.fetchPiezometersContributors()),
    ], [])

    const jobFilters = useMemo(() => ({ groupMode: MAX, ...(job.parameters.filters.length ? JSON.parse(job.parameters.filters[0]) : {}) }), [job.parameters.filters])

    const onChangeJobParameter = (changes) => onChangeJob({ parameters: { ...job.parameters, ...changes } })

    const onChangeFilters = (changes) => onChangeJob({ parameters: { ...job.parameters, filters: [JSON.stringify({ ...jobFilters, ...changes })] } })

    const contributorsFormatted = useMemo(() => contributors.filter(c => c.siret?.length > 0).map((c) => ({
        ...c,
        nameFormatted: `${c?.siret} - ${c?.name || ''}`,
    })), [contributors])

    const selectedStations = useMemo(() => piezometers.filter(p => (jobFilters.stations || []).includes(p.id)), [jobFilters.stations, piezometers])

    const [list, setList] = useState(selectedStations)

    const pointsPrel = useMemo(() => Array.isArray(jobFilters.pointsPrel) ? jobFilters.pointsPrel : [], [jobFilters.pointsPrel])

    const internalReferences = useMemo(() => Array.isArray(jobFilters.withInternalReference) ? jobFilters.withInternalReference : [], [jobFilters.withInternalReference])

    const internalReferencesByStation = useMemo(() => {
        const activeLinks = piezometersContributors.filter(link => {
            const reference = link.internalReference?.trim()
            return !!reference && (!link.startDate || link.startDate < Date.now()) && (!link.endDate || link.endDate >= Date.now())
        })
        return mapValues(groupBy(activeLinks, 'idStation'), links => uniq(links.map(link => link.internalReference.trim())))
    }, [piezometersContributors])

    if (!isLoaded) {
        return <ProgressCard progress={progress} />
    }

    return (
        <>
            <Grid container spacing={1}>
                <Grid size={4}>
                    <Input
                        title={i18n.directory}
                        value={job.parameters.path}
                        onChange={v => onChangeJobParameter({ path: v })}
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={4}>
                    <Select
                        options={exportModes}
                        label={i18n.exportMode}
                        onChange={v => onChangeFilters({ mode: v })}
                        value={jobFilters.mode}
                        disabled={!isEditMode}
                        tooltip={i18n.exportQesoutTip}
                    />
                </Grid>
                <Grid size={4}>
                    <Select
                        options={groupModes}
                        label={i18n.regrouping}
                        onChange={v => onChangeFilters({ groupMode: v })}
                        value={jobFilters.groupMode}
                        noNullValue
                        disabled={!isEditMode}
                    />
                </Grid>
                {(() => {
                    if (jobFilters.mode === CUSTOM) {
                        return [
                            <Grid size={2} key='startDate'>
                                <SimpleDatePicker
                                    label={i18n.startDate}
                                    value={jobFilters.startDate}
                                    onChange={v => onChangeFilters({ startDate: v })}
                                    disabled={!isEditMode}
                                />
                            </Grid>,
                            <Grid size={2} key='endDate'>
                                <SimpleDatePicker
                                    label={i18n.endDate}
                                    value={jobFilters.endDate}
                                    onChange={v => onChangeFilters({ endDate: v })}
                                    disabled={!isEditMode}
                                />
                            </Grid>,
                        ]
                    }
                    if (jobFilters.mode === UPDATE_DATE) {
                        return (
                            <Grid size={4}>
                                <SimpleDatePicker
                                    label={i18n.updateDate}
                                    value={jobFilters.updateDateModeDate}
                                    onChange={v => onChangeFilters({ updateDateModeDate: v })}
                                    disabled={!isEditMode}
                                />
                            </Grid>
                        )
                    }
                    return <Grid size={4}/>
                })()}
            </Grid>
            <Grid container spacing={1} alignItems={'center'}>
                <Grid size={4}>
                    <MultiContributorsAutocomplete
                        label={i18n.siret}
                        values={jobFilters.siret}
                        onChange={c => onChangeFilters({ siret: c })}
                        options={contributorsFormatted}
                        keyLabel='nameFormatted'
                        keyValue='siret'
                        disabled={!isEditMode}
                    />
                </Grid>
                <Grid size={4}>
                    <MultiContributorsAutocomplete
                        options={contributors}
                        label={i18n.producer}
                        values={jobFilters.producer}
                        onChange={c => onChangeFilters({ producer: c })}
                        keyLabel='mnemonique'
                        disabled={!isEditMode}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1} pb={1}>
                <Grid size={4}>
                    <RadioButtons
                        title={i18n.selectStations}
                        elements={getStationModes()}
                        selected={jobFilters.selectStationsMode || FILTERS}
                        onChange={v => onChangeFilters({ selectStationsMode: v })}
                        disabled={!isEditMode}
                    />
                </Grid>
            </Grid>
            {!jobFilters.selectStationsMode || jobFilters.selectStationsMode === FILTERS ? (
                <Grid container spacing={1}>
                    <Grid size={4}>
                        <Select
                            options={networks}
                            label={i18n.network}
                            value={jobFilters.network}
                            onChange={c => onChangeFilters({ network: c })}
                            disabled={!isEditMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={cities}
                            label={i18n.city}
                            value={jobFilters.city}
                            onChange={c => onChangeFilters({ city: c })}
                            disabled={!isEditMode}
                            keyLabel='labelWithCode'
                        />
                    </Grid>
                    <Grid size={4}>
                        <SimpleFilterSelect
                            onChange={(_, f) => onChangeFilters({ stationsFilter: f && f !== -1 ? f : null })}
                            stations={piezometers}
                            stationType={STATION_TYPE_NAME.piezometry}
                            disabled={!isEditMode}
                            value={jobFilters.stationsFilter}
                            ignoreCache
                        />
                    </Grid>
                </Grid>
            ) : (
                <StationsPointPrelTable
                    stations={selectedStations}
                    pointsPrel={pointsPrel}
                    points={pointsPrelList}
                    onChange={v => onChangeFilters({ pointsPrel: v })}
                    disabled={!isEditMode}
                    actions={[{ icon: 'edit', onClick: () => setIsOpen(true), color: 'white' }]}
                    codeOptions={internalReferencesByStation}
                    selectedCodes={internalReferences}
                    onChangeCodes={v => onChangeFilters({ withInternalReference: v })}
                />
            )}
            <Dialog open={isOpen} fullWidth maxWidth='lg'>
                <DefaultDialogTitle
                    onClose={() => {
                        setList(selectedStations)
                        setIsOpen(false)
                    }}
                    title={i18n.selectStationsToBeExported}
                />
                <DialogContent>
                    <SelectedElementsModal
                        selectedList={list}
                        onChange={setList}
                        list={piezometers}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        onClick={() => {
                            onChangeFilters({ stations: list.map(p => p.id) })
                            setIsOpen(false)
                        }}
                    >
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

ExportQesoutPanel.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    onChangeJob: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
}

export default ExportQesoutPanel